import { createAsyncThunk } from '@reduxjs/toolkit';
import { z } from 'zod';

import { getEnvironmentConfig, getHostConfig, httpClientInterface, isDemoMode, tracker } from '../../utils/core';
import { getOAuthTokenSuccess } from './authActions';
import { createRefreshTokenRequest } from './authRefreshTokenRequest';
import { AuthTokenData } from './authTypes';

const futureExpiry = z.string().refine((value) => Date.parse(value) > Date.now());
const opaqueRefreshTokensSchema = z.object({
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1),
  accessTokenExpired: futureExpiry,
  refreshTokenExpired: futureExpiry,
  scope: z.string().optional().catch(undefined),
  surveyRequested: z.boolean().optional().catch(undefined),
});
const mintedOpaqueRefreshTokenSchema = z.object({ refreshToken: z.string().min(1) });

async function postOAuthToken<T>({
  servicesUrl,
  data,
  headers,
  parse,
  failure,
  config = {},
}: {
  servicesUrl: string;
  data: unknown;
  headers: Record<string, string | null>;
  parse: (data: unknown) => T;
  failure: string;
  config?: Record<string, boolean>;
}): Promise<T> {
  try {
    const response = await httpClientInterface.send(
      'POST',
      `${servicesUrl}/oauth/token`,
      data,
      { 'Content-Type': 'application/json', ...headers },
      undefined,
      { timeout: 30_000, withCredentials: false, ...config },
    );
    if (response.status < 200 || response.status >= 300) throw new Error(failure);
    return parse(response.data);
  } catch {
    // Never propagate a transport error/response containing request credentials.
    throw new Error(failure);
  }
}

// Mints a one-use opaque refresh token for the current user, to be redeemed by another client.
export async function mintOpaqueRefreshToken({
  accessToken,
  targetClientId,
}: {
  accessToken: string;
  targetClientId: string;
}): Promise<string> {
  const failure = 'Opaque refresh token mint failed';
  const servicesUrl = getEnvironmentConfig()?.services_url;
  if (!servicesUrl) throw new Error(failure);
  return postOAuthToken({
    servicesUrl,
    data: { grantType: 'qcs_user_token', clientId: targetClientId },
    headers: { Authorization: `Bearer ${accessToken}`, 'app-client-id': targetClientId },
    parse: (data) => mintedOpaqueRefreshTokenSchema.parse(data).refreshToken,
    failure,
  });
}

export const redeemOpaqueRefreshToken = createAsyncThunk<ReturnType<typeof AuthTokenData>, string>(
  'auth/redeemOpaqueRefreshToken',
  async (refreshToken, { dispatch }) => {
    const environment = getEnvironmentConfig();
    const clientId = environment?.client_id;
    const clientSecret = environment?.client_secret;
    let redirectUri = getHostConfig()?.redirect_uri;
/////////////////////////////////////////////
    // IP-based preview hosts have no host config, so they use localhost's registered redirect URI.
    redirectUri ||= getHostConfig('localhost')?.redirect_uri;
/////////////
    if (!environment?.services_url || !clientId || !clientSecret || !redirectUri)
      throw new Error('Opaque refresh token configuration missing');
    if (!refreshToken.trim()) throw new Error('Opaque refresh token missing');
    const request = createRefreshTokenRequest({ refreshToken, redirectUri, clientId, clientSecret });
    // Validate once before the OAuth success action can modify auth state.
    const tokens = await postOAuthToken({
      servicesUrl: environment.services_url,
      data: request,
      headers: { Authorization: null, 'app-client-id': clientId },
      parse: (data) => AuthTokenData(opaqueRefreshTokensSchema.parse(data)),
      failure: 'Opaque refresh token exchange failed',
      config: { opaqueRefreshTokenRedemption: true },
    });
    tracker.resetActiveTimestamp();
    dispatch(getOAuthTokenSuccess(tokens.set('surveyRequested', isDemoMode() ? false : tokens.surveyRequested)));
    if (tokens.surveyRequested) tracker.track(tracker.events.npsRequested);
    return tokens;
  },
);
